import type { ElementType, ReactNode } from 'react';
import { PRETTY, riseAt, type Tone } from './ui-kit';
import { Icon, type IconName } from './icon';

/**
 * Presentational pieces shared by the Eval and RAG screens.
 *
 * These screens are read-heavy: a run is mostly numbers, a RAG version is mostly
 * configuration. What makes them legible is that a label always looks like a
 * label, a number always aligns with the number above it, and a status always
 * carries the same colour. Keeping that vocabulary in one file is what stops it
 * drifting screen by screen.
 *
 * The surface, tone and motion classes live in index.css next to the theme
 * tokens; only the composition lives here.
 */

/** Section heading inside a panel: small, quiet, and set apart by tracking. */
export const SectionLabel = ({ children, icon, className = '' }: {
  children: ReactNode; icon?: string; className?: string;
}) => (
  <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted ${className}`}>
    {icon && <i className={`${icon} text-[11px]`} aria-hidden />}
    <span>{children}</span>
  </div>
);

/**
 * One labelled value. Numbers get tabular figures so a column of them lines up
 * and a live one does not shift the row as it counts.
 */
export const Metric = ({ label, value, tone, hint, mono = false }: {
  label: string; value: ReactNode; tone?: Tone; hint?: string | null; mono?: boolean;
}) => (
  <div className="min-w-0">
    {/* ink-muted (slate-500), not a lighter grey: an 11px label needs 4.5:1. */}
    <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">{label}</div>
    <div
      // The value truncates, so the full text has to stay reachable somewhere.
      title={hint || (typeof value === 'string' || typeof value === 'number' ? String(value) : undefined)}
      className={[
        'mt-0.5 text-[15px] font-semibold tabular-nums truncate',
        mono ? 'font-mono text-[13px]' : '',
        tone === 'success' ? 'text-success'
          : tone === 'danger' ? 'text-error'
          : tone === 'warn' ? 'text-warning'
          : 'text-ink',
      ].join(' ')}
    >
      {value}
    </div>
  </div>
);

/** Status pill. One dot, one word, the same colour for the same meaning everywhere. */
export const StatusChip = ({ tone = 'neutral', children, dot = true, className = '', testId }: {
  tone?: Tone; children: ReactNode; dot?: boolean; className?: string; testId?: string;
}) => (
  <span className={`ui-tone ui-tone--${tone} ${className}`} data-testid={testId}>
    {dot && <span className="ui-tone__dot" aria-hidden />}
    {children}
  </span>
);

/**
 * Staggers its children in on mount: each chunk arrives a beat after the one
 * above, which reads as the page assembling rather than flashing into place.
 * `start` offsets the sequence when a page has more than one Rise group.
 */
export const Rise = ({ children, i = 0, className = '', as: Tag = 'div' }: {
  children: ReactNode; i?: number; className?: string; as?: ElementType;
}) => (
  <Tag className={`ui-rise ${className}`} style={riseAt(i)}>{children}</Tag>
);


/** Empty state: says what is missing and what to do about it, never just "no data". */
export const EmptyState = ({ icon, title, hint, action }: {
  icon: string; title: string; hint?: string; action?: ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
    <span className="ui-inset flex h-11 w-11 items-center justify-center text-ink-muted">
      <i className={`${icon} text-lg`} aria-hidden />
    </span>
    <p className="text-sm font-medium text-ink">{title}</p>
    {hint && <p className="max-w-sm text-xs leading-relaxed text-ink-muted" style={PRETTY}>{hint}</p>}
    {action}
  </div>
);

/**
 * Page identity: a 40×40 back target, the name, a meta row (code, chips) and
 * the actions that change it. Views and editors share this one shape so moving
 * between a RAG, a suite and a case never changes the furniture.
 */
export const PageTitle = ({ onBack, backLabel, title, meta, actions, className = '' }: {
  onBack: () => void; backLabel: string; title: ReactNode; meta?: ReactNode; actions?: ReactNode; className?: string;
}) => (
  <Rise className={`mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 ${className}`}>
    {/* Arrow and name are one unit: on a phone the actions wrap below them,
        rather than the arrow being orphaned on a line of its own. The basis is
        what forces that wrap — without it the name shrinks toward nothing to
        keep two buttons on the same line. */}
    <div className="flex min-w-0 flex-1 basis-64 items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        aria-label={backLabel}
        className="ui-press -ml-2 flex h-10 w-10 flex-none items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-canvas hover:text-ink"
      >
        <i className="pi pi-arrow-left text-sm" aria-hidden />
      </button>
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold tracking-[-0.01em] text-ink" title={typeof title === 'string' ? title : undefined}>{title}</h1>
        {meta && <div className="mt-1 flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </Rise>
);

/**
 * Loading state in the page's own shape: a title line, then panels holding
 * metric stubs. It keeps the height the real page will take and says what is
 * coming, where a centred spinner only says "wait" and then jumps.
 */
export const PageSkeleton = ({ panels = 2, className = 'p-4' }: { panels?: number; className?: string }) => (
  <div className={className} aria-busy="true" aria-label="Loading">
    <div className="mb-4 flex items-center gap-3">
      <span className="ui-skel h-10 w-10 !rounded-lg" />
      <span className="ui-skel h-6 w-56 !rounded-md" />
    </div>
    <div className="flex flex-col gap-3">
      {Array.from({ length: panels }, (_, i) => (
        <div key={i} className="ui-panel ui-rise p-4" style={riseAt(i)}>
          <span className="ui-skel h-2.5 w-20" />
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
            {[0, 1, 2, 3].map((k) => (
              <span key={k} className="block">
                <span className="ui-skel h-2 w-14" />
                <span className="ui-skel mt-2 h-4 w-24 !rounded-md" />
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * The page-level button. Primary is a solid ink fill and appears once per
 * region; secondary is a surface with a hairline ring; quiet and danger are
 * typographic until hovered. `sm` is for actions inside a row.
 */
export const ActionButton = ({ kind = 'secondary', size, icon, label, onClick, disabled, loading, testId, type = 'button' }: {
  kind?: 'primary' | 'secondary' | 'quiet' | 'danger'; size?: 'sm'; icon?: IconName; label: string;
  onClick?: () => void; disabled?: boolean; loading?: boolean; testId?: string; type?: 'button' | 'submit';
}) => (
  <button type={type} onClick={onClick} disabled={disabled || loading} data-testid={testId} aria-busy={loading || undefined}
    className={`ui-press ui-btn ui-btn--${kind} ${size === 'sm' ? 'ui-btn--sm' : ''}`}>
    {loading ? <span className="ui-btn__spin animate-spin" aria-hidden /> : icon && <Icon name={icon} size={size === 'sm' ? 14 : 15} />}
    {label}
  </button>
);
